import { useDisclosure } from '@agrotoken-tech/ui';
import { PERFORM_ACTION } from '@bcba/casl-authorization';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import { useBreakpointValue } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../config/auth/auth';
import { useUserContext } from '../../../contexts/UserContext';
import { paths, RouteKey } from '../../../routes/paths';

interface Link {
  id: number;
  label: string;
  showBadge?: boolean;
  badgeText?: string;
  onClick: () => void;
}
interface HeaderProps {
  fullName: string;
  email?: string;
  companyName?: string;
  handleSignOut: () => Promise<void>;
  links: Link[];
  currentLinkId: number | null;
  navigateTo: (path: string) => void;
  cancelAlertPendingChangeModal: () => void;
  confirmAlertPendingChangeModal: () => void;
  isPendingChangeAlertOpen: boolean;
}
const ROUTES = {
  home: { id: 0, path: paths.home },
  receivedAccessRequests: { id: 1, path: paths.receivedAccessRequests },
  dossierExplorer: { id: 2, path: paths.dossierExplorer },
  sharedDigitalDossier: { id: 2, path: paths.sharedDigitalDossier },
  cropPledge: { id: 3, path: paths.cropPledge },
  cropPledgeDetail: { id: 3, path: paths.cropPledgeDetail },
  profile: { id: 4, path: paths.profile },
  signup: { id: 0, path: paths.signup },
};

export const useHeader = (): HeaderProps => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { showErrorToast } = useToastsHook();
  const { cleanUpSession } = useAuth();
  const queryClient = useQueryClient();

  const {
    user,
    ability,
    isSignUpRequestApproved,
    refetchUser,
    userHasPendingChanges,
    setUserHasPendingChanges,
  } = useUserContext();

  const {
    isOpen: isPendingChangeAlertOpen,
    onOpen: onPendingChangeAlertOpen,
    onClose: onPendingChangeAlertClose,
  } = useDisclosure();

  const {
    explorerEnableDigitalDossiersExplorer,
    explorerEnableReceivedAccessRequests,
    explorerEnableCropPledges,
  } = useFlags();

  const shouldHideReceivedAccessRequests = useBreakpointValue({
    base: false,
    sm: false,
    md: true,
    lg: false,
    xl: false,
  });

  const [path, setPath] = useState<string | undefined>('');
  const [tabIndex, setTabIndex] = useState<number | null>(null);

  const fullName = user?.firstName + ' ' + user?.lastName;

  const handleSignOut = async () => {
    try {
      if (cleanUpSession) {
        queryClient.clear();
        cleanUpSession();
      }
    } catch (error) {
      showErrorToast({
        title: t('common.toast.error.title'),
        text: t('common.toast.error.text'),
      });
    }
  };

  const navigateTo = useCallback(
    (path: string) => {
      refetchUser();
      navigate(path);
    },
    [navigate, refetchUser],
  );

  useEffect(() => {
    const matchRoute = () => {
      // Loop through the routes and check if the current path matches any of them
      (Object.keys(ROUTES) as Array<RouteKey>).forEach((routeKey) => {
        const route = ROUTES[routeKey];

        // Special case: manually check if path is exactly '/'
        if (route.path === paths.home && location.pathname === paths.home) {
          setTabIndex(route.id);
          return;
        }

        // Match other paths including dynamic segments
        if (matchPath(route.path, location.pathname)) {
          setTabIndex(route.id);
          return;
        }
      });
    };

    matchRoute();
  }, [location.pathname]);

  const cancelAlertPendingChangeModal = () => {
    onPendingChangeAlertClose();
  };

  const confirmAlertPendingChangeModal = useCallback(() => {
    setUserHasPendingChanges(false);
    navigateTo(path ?? '');
    onPendingChangeAlertClose();
  }, [navigateTo, onPendingChangeAlertClose, path, setUserHasPendingChanges]);

  const handleOnClick = useCallback(
    (path?: string) => {
      setPath(path);
      if (userHasPendingChanges) {
        onPendingChangeAlertOpen();
      } else {
        if (path) navigateTo(path);
      }
    },
    [navigateTo, onPendingChangeAlertOpen, userHasPendingChanges],
  );

  const links: Link[] = useMemo(() => {
    const links: Link[] = [];
    const prefix = 'explorer.navigation.route';

    if (isSignUpRequestApproved) {
      if (ability?.can(PERFORM_ACTION.Read, 'DigitalDossier'))
        links.push({
          id: ROUTES.home.id,
          label: t(`${prefix}.home`),
          onClick: () => handleOnClick(ROUTES.home.path),
        });
      if (
        !shouldHideReceivedAccessRequests &&
        explorerEnableReceivedAccessRequests &&
        ability?.can(PERFORM_ACTION.List, 'DigitalDossierReceivedAccessRequest')
      )
        links.push({
          id: ROUTES.receivedAccessRequests.id,
          label: t(`${prefix}.receivedAccessRequests`),
          onClick: () => handleOnClick(ROUTES.receivedAccessRequests.path),
        });
      if (
        explorerEnableDigitalDossiersExplorer &&
        ability?.can(PERFORM_ACTION.List, 'DigitalDossier')
      )
        links.push({
          id: ROUTES.dossierExplorer.id,
          label: t(`${prefix}.dossierExplorer`),
          onClick: () => handleOnClick(ROUTES.dossierExplorer.path),
        });
      if (
        ability?.can(PERFORM_ACTION.List, 'CropPledge') &&
        explorerEnableCropPledges
      ) {
        links.push({
          id: ROUTES.cropPledge.id,
          label: t('common.navigation.route.cropPledge'),
          onClick: () => navigateTo(ROUTES.cropPledge.path),
        });
      }
    } else {
      if (ability?.can(PERFORM_ACTION.Read, 'SignupRequest'))
        links.push({
          id: ROUTES.home.id,
          label: t(`${prefix}.signUpRequest`),
          onClick: () => handleOnClick(ROUTES.home.path),
        });
    }

    return links;
  }, [
    isSignUpRequestApproved,
    ability,
    t,
    shouldHideReceivedAccessRequests,
    explorerEnableReceivedAccessRequests,
    explorerEnableDigitalDossiersExplorer,
    explorerEnableCropPledges,
    handleOnClick,
    navigateTo,
  ]);

  return {
    fullName,
    email: user?.email,
    companyName: user?.companyName,
    handleSignOut,
    links,
    currentLinkId: tabIndex,
    navigateTo,
    cancelAlertPendingChangeModal,
    confirmAlertPendingChangeModal,
    isPendingChangeAlertOpen,
  };
};

export default useHeader;
